import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { useContinuousData } from '../../../../hooks/useContinuousData';
import { useUser } from '../../../../hooks/useUser';
import {
    CONTINUOUS_HEART_RATE_TYPE,
    getContinuousHeartRateRange,
    normalizeContinuousHeartRate
} from './continuousHeartRate';

const HeartRateEvolutionChart = ({ patientUuid, patientCreated, period }) => {
    const { t, i18n } = useTranslation();
    const { user } = useUser();
    const zone = user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const range = useMemo(
        () => getContinuousHeartRateRange({ period, patientCreated, zone }),
        [period, patientCreated, zone]
    );
    const query = useContinuousData({
        patientUuid,
        dtype: CONTINUOUS_HEART_RATE_TYPE,
        ...range
    });
    const data = useMemo(
        () => normalizeContinuousHeartRate(query.data, range),
        [query.data, range]
    );
    const locale = i18n.resolvedLanguage?.startsWith('pt') ? 'pt-PT' : 'en-GB';
    const shortDateFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(locale, {
                day: 'numeric',
                month: 'short',
                timeZone: zone,
                ...(period === '7d' ? { hour: '2-digit', minute: '2-digit' } : {})
            }),
        [locale, zone, period]
    );
    const longDateFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(locale, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: zone
            }),
        [locale, zone]
    );

    if (query.isPending)
        return (
            <Box sx={{ display: 'grid', height: 300, gap: 1 }}>
                <Skeleton width="28%" height={20} />
                <Skeleton variant="rounded" height={260} />
            </Box>
        );
    if (query.isError)
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    height: 300
                }}
            >
                <Typography color="error" variant="body2">
                    {t('userPage.overview.rehabilitation.heartRateChart.loadError')}
                </Typography>
                <Button size="small" variant="outlined" onClick={() => query.refetch()}>
                    {t('userPage.overview.measurements.retry')}
                </Button>
            </Box>
        );
    if (!data.length)
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 300
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    {t('userPage.overview.rehabilitation.heartRateChart.noData')}
                </Typography>
            </Box>
        );

    return (
        <Box
            role="img"
            aria-label={t('userPage.overview.rehabilitation.heartRateChart.chartLabel')}
            sx={{ width: '100%', height: 300 }}
        >
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    color: 'text.secondary',
                    fontWeight: 600,
                    mb: 0.5
                }}
            >
                {t('userPage.overview.rehabilitation.heartRateChart.subtitle')}
            </Typography>
            <ResponsiveContainer width="100%" height={278}>
                <LineChart
                    data={data}
                    margin={{ top: 10, right: 12, left: 0, bottom: 0 }}
                >
                    <CartesianGrid
                        vertical={false}
                        stroke="#E6EDF2"
                        strokeDasharray="3 3"
                    />
                    <XAxis
                        dataKey="timestamp"
                        type="number"
                        scale="time"
                        domain={([min, max]) =>
                            min === max ? [min - 150000, max + 150000] : [min, max]
                        }
                        tickCount={6}
                        minTickGap={28}
                        axisLine={{ stroke: '#CAD5DD' }}
                        tickLine={false}
                        tick={{ fill: '#74838E', fontSize: 10 }}
                        tickFormatter={(value) =>
                            shortDateFormatter.format(new Date(value))
                        }
                    />
                    <YAxis
                        width={48}
                        domain={['auto', 'auto']}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#74838E', fontSize: 10 }}
                        label={{
                            value: t(
                                'userPage.overview.rehabilitation.heartRateChart.axisLabel'
                            ),
                            angle: -90,
                            position: 'insideLeft',
                            fill: '#74838E',
                            fontSize: 10
                        }}
                    />
                    <Tooltip
                        labelFormatter={(value) =>
                            longDateFormatter.format(new Date(value))
                        }
                        formatter={(value, name) => [`${value} bpm`, name]}
                        contentStyle={{
                            border: '1px solid #D7E0E6',
                            borderRadius: 6,
                            boxShadow: '0 4px 12px rgba(38, 50, 56, 0.12)',
                            fontSize: 11
                        }}
                    />
                    <Legend
                        verticalAlign="top"
                        align="right"
                        height={24}
                        iconType="plainline"
                        iconSize={14}
                        wrapperStyle={{ color: '#5E6C76', fontSize: 10 }}
                    />
                    <Line
                        type="linear"
                        dataKey="hr"
                        name={t(
                            'userPage.overview.rehabilitation.heartRateChart.fiveMinuteAverage'
                        )}
                        stroke="#0A6FB4"
                        strokeWidth={2}
                        dot={
                            data.length <= 200
                                ? { r: 2.5, fill: '#0A6FB4', strokeWidth: 0 }
                                : false
                        }
                        activeDot={{ r: 4, strokeWidth: 0 }}
                        connectNulls={false}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </Box>
    );
};

HeartRateEvolutionChart.propTypes = {
    patientUuid: PropTypes.string.isRequired,
    patientCreated: PropTypes.string,
    period: PropTypes.oneOf(['7d', '30d', '90d', 'all']).isRequired
};

export default HeartRateEvolutionChart;
